import { observable, computed, reaction, action, runInAction, asMap } from 'mobx'

import { get } from 'transport/project'

import UiStore from 'stores/UiStore'

import { debounce, projectSubmodelFromJS } from 'utils'

class ProjectStore {
  @observable triggerCount = 0

  @observable fullProjects = asMap({})

  @observable projectsMap = asMap({})

  @computed get projects () {
    const pageIds = this.pages.get(UiStore.pagination.current)

    if (!pageIds) {
      return []
    }

    return pageIds.map(id => this.projectsMap.get(id)).filter(a => a)
  }

  @observable pages = asMap({})

  constructor () {
    // mobx reaction disposers
    this.fetcher = null
    this.filterReset = null
  }

  @action async fetchProjects () {
    // projectFilters don't contain types yet so we need to attach types to projectFilters
    const filtersWithTypes = UiStore.projectFiltersWithType
    const response = await get(UiStore.pagination.current, filtersWithTypes)
    const toBeMergedList = response.projects
      .map(projectSubmodelFromJS)

    runInAction(() => {
      // update pagination
      UiStore.pagination.current = response.current_page
      UiStore.pagination.last = response.last_page

      // merge new projects into this.projectsMap
      for (let project of toBeMergedList) {
        this.projectsMap.set(project.id, project)
      }

      // remember the ordered project ids for this page
      this.pages.set(UiStore.pagination.current, toBeMergedList.map(({ id }) => id))
    })
  }

  startFetcher () {
    this.fetchProjects()

    this.filterReset = reaction(
      () => [
        UiStore.projectFilters.sort,
        UiStore.projectFilters.search,
        UiStore.projectFilters.faculty,
        UiStore.projectFilters.department,
        UiStore.projectFilters.school,
        UiStore.projectFilters.tag,
        UiStore.projectType
      ],
      debounce(::this.restart, 500)
    )

    this.fetcher = reaction(
      // dependencies to watch
      () => [
        this.triggerCount,
        UiStore.pagination.current
      ],
      // action
      () => {
        if (!this.pages.get(UiStore.pagination.current)) {
          this.fetchProjects()
        }
      }
    )
  }

  @action restart () {
    // The following will trigger a fetcher (below) reaction
    // clear projects from memory
    this.clearProjects()

    // reset pagination
    UiStore.resetPagination()

    // trigger fetch reaction
    this.triggerCount += 1

    UiStore.updateProjectFiltersInUrlBar()
  }

  @action stopFetcher () {
    if (this.fetcher) {
      // dispose reactions
      this.fetcher()
      this.filterReset()

      // clear from memory
      this.clearProjects()

      // reset pagination
      UiStore.resetPagination()

      // reset filters
      UiStore.resetProjectFilters()
    }
  }

  @action clearProjects () {
    this.projectsMap.clear()
    this.clearPages()
  }

  clearPages () {
    this.pages.clear()
  }
}

const singleton = new ProjectStore()
export default singleton



// WEBPACK FOOTER //
// ./resources/assets/js/stores/ProjectStore.js